import { Component } from "react";
import {
  getHourBandPhrase,
  getPopularMoods,
  rankMoods,
} from "../shuffle-moods";
import {
  getMoodPlays,
  getSlowedMemory,
  recordMoodUsed,
} from "../slowed-memory";
import ChipRow from "./chip-row";

export default class Mood extends Component {
  constructor(props) {
    super(props);
    // Sorted by the hour straight away, then again once what this person
    // plays has loaded — both before they have had time to reach for a chip.
    this.state = { moods: this.rank() };
  }

  componentDidMount() {
    // The sheet now mounts already open, so there is no false→true update.
    if (this.props.open && !this.props.isProFeaturesLocked) {
      this.props.onOpen?.({ source: "section" });
    }
    this.mounted = true;
    this.rankWithMemory();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      if (!this.props.isProFeaturesLocked) {
        this.props.onOpen?.({ source: "section" });
      }
      this.rankWithMemory();
    }
  }

  rank(memory) {
    return rankMoods(getPopularMoods(), {
      now: this.props.now || new Date(),
      history: getMoodPlays(memory),
      likes: memory?.likes,
      speedUpMode: !!this.props.speedUpMode,
    });
  }

  // Ranks only on open, never on a pick: re-sorting under the pointer would
  // move the chip that was just tapped.
  async rankWithMemory() {
    let memory;
    try {
      memory = await getSlowedMemory();
    } catch {
      return;
    }
    if (this.mounted) this.setState({ moods: this.rank(memory) });
  }

  // "None" is the state the controls are already in when no mood is applied,
  // so it is never a Pro action and never reads as locked — picking it just
  // puts the controls back where the mood found them.
  async pickNone() {
    if (!this.props.selectedMoodName) return;
    try {
      await this.props.onClearMood?.();
    } catch {
      // Ignore apply errors; keep the sheet open.
    }
  }

  async pickMood(mood) {
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "mood", mood: mood?.name });
      this.props.onUpgradeClick?.();
      return;
    }

    if (this.props.selectedMoodName === mood.name) {
      try {
        await this.props.onClearMood?.();
      } catch {
        // Ignore apply errors; keep the sheet open.
      }
      return;
    }

    try {
      await this.props.onApplyMood?.(mood);
      await recordMoodUsed(mood.name);
    } catch {
      // Ignore apply errors; keep the sheet open.
    }
  }

  render() {
    const { open, isProFeaturesLocked, selectedMoodName } = this.props;
    const { moods } = this.state;
    const locked = !!isProFeaturesLocked;
    const noneSelected = !selectedMoodName;

    return (
      <div
        id="mood-sheet"
        className={`mood${open ? " mood--open" : ""}`}
        aria-label="Moods"
        aria-hidden={!open}
        {...(!open ? { inert: true } : {})}
      >
        <div className="mood__header">
          <div className="content__title">Moods</div>
          <p className="mood__subtitle">
            Pick a mood, sorted for {getHourBandPhrase(this.props.now)} and
            what you play most.
          </p>
        </div>

        <ChipRow>
          <button
            key="__none"
            type="button"
            className={`mood__chip${noneSelected ? " mood__chip--selected" : ""}`}
            onClick={() => this.pickNone()}
            title="No mood — leave the controls as they are"
            aria-pressed={noneSelected}
            tabIndex={open ? 0 : -1}
          >
            <span className="mood__chip-name">None</span>
          </button>

          {moods.map((mood) => {
            const isSelected = selectedMoodName === mood.name;
            return (
              <button
                key={mood.name}
                type="button"
                className={`mood__chip${isSelected ? " mood__chip--selected" : ""}${locked ? " mood__chip--locked" : ""}`}
                onClick={() => this.pickMood(mood)}
                aria-disabled={locked || undefined}
                title={mood.name}
                aria-pressed={isSelected}
                tabIndex={open ? 0 : -1}
              >
                <span className="mood__chip-name">{mood.name}</span>
              </button>
            );
          })}
        </ChipRow>
      </div>
    );
  }
}
